import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaUsers,
  FaBuilding,
  FaUserCheck,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserTimes,
  FaPlusCircle,
  FaStarHalfAlt,
  FaStar,
  FaBookOpen,
  FaHandshake,
  FaExternalLinkAlt,
  FaSpinner,
  FaSyncAlt,
  FaArrowCircleRight,
  FaChartBar,
  FaComments,
  FaClipboardList,
  FaMapMarkerAlt,
} from "react-icons/fa";
import api from "../../services/axios";
import { formatarDiaHora, formatarHora } from "../../services/conversaoTimer";
import ListaTabelaModal from "../../components/listaTabelaModal";
import AdmDashEstatisticas, {
  ESTATISTICAS_VAZIAS,
} from "./admDashEstatisticas";
import AdmDashAgendamentos from "./admDashAgendamentos";
import AdmDashInteracoes, {
  INTERACOES_VAZIAS,
} from "./admDashInteracoes";
import "./adm.css";
import "./admPatrocinadoresList.css";
import "./admDash.css";
import "../../components/modal/modal.css";

const CREDENCIADO_LISTA_COLS = [
  { key: "nome", label: "Nome", excelLabel: "Nome" },
  {
    key: "empresa",
    label: "Empresa",
    excelLabel: "Empresa",
    excelValue: (row) => row.empresa || "",
    render: (row) =>
      row.empresa ? (
        row.empresa
      ) : (
        <span className="ltm-muted">Não informada</span>
      ),
  },
  { key: "email", label: "E-mail", excelLabel: "E-mail" },
  {
    key: "tipo",
    label: "Tipo",
    excelLabel: "Tipo",
    excelValue: (row) =>
      row.tipo === "participante_patrocinador"
        ? "Participante patrocinador"
        : "Convidado",
    render: (row) =>
      row.tipo === "participante_patrocinador"
        ? "Participante patrocinador"
        : "Convidado",
  },
];

const LABEL_PESQUISA_CONTEUDO = {
  1: "1 estrela",
  2: "2 estrelas",
  3: "3 estrelas",
  4: "4 estrelas",
  5: "5 estrelas",
};

function formatarRespostaPesquisaConteudo(row) {
  if (row?.pesquisaRespondida && row.rate != null) {
    return LABEL_PESQUISA_CONTEUDO[row.rate] || `${row.rate} estrelas`;
  }
  if (row?.checkin) {
    return "Pendente";
  }
  return "";
}

function renderCelulaPesquisa(label) {
  if (!label) {
    return <span className="ltm-muted">Não respondida</span>;
  }
  if (label === "Pendente") {
    return <span className="ltm-muted">Pendente</span>;
  }
  return label;
}

function respostaPesquisaPorTipo(row, tipoEsperado) {
  if (row?.tipo !== tipoEsperado) {
    return "";
  }
  return formatarRespostaPesquisaConteudo(row);
}

const COLUNAS_PESQUISA_CONTEUDO = [
  {
    key: "respostaConvidado",
    label: "Resposta convidado",
    excelLabel: "Resposta convidado",
    excelValue: (row) => respostaPesquisaPorTipo(row, "convidado"),
    excelEmptyValue: "—",
    render: (row) => {
      if (row?.tipo !== "convidado") {
        return <span className="ltm-muted">—</span>;
      }
      return renderCelulaPesquisa(formatarRespostaPesquisaConteudo(row));
    },
  },
  {
    key: "respostaPatrocinador",
    label: "Resposta patrocinador",
    excelLabel: "Resposta patrocinador",
    excelValue: (row) =>
      respostaPesquisaPorTipo(row, "participante_patrocinador"),
    excelEmptyValue: "—",
    render: (row) => {
      if (row?.tipo !== "participante_patrocinador") {
        return <span className="ltm-muted">—</span>;
      }
      return renderCelulaPesquisa(formatarRespostaPesquisaConteudo(row));
    },
  },
];

const ACAO_ESPECIAL_INTERESSADOS_COLS = CREDENCIADO_LISTA_COLS;
const SALA_CONTEUDO_PARTICIPANTES_COLS = [
  ...CREDENCIADO_LISTA_COLS,
  ...COLUNAS_PESQUISA_CONTEUDO,
];

const LABEL_NOTA_REUNIAO = {
  2: "Não foi boa",
  3: "Aproximou relacionamento",
  4: "Gerou proposta",
  5: "Vamos fechar negócio",
};

function formatarNotaReuniao(nota) {
  if (nota == null || nota === "") {
    return null;
  }
  const num = Number(nota);
  if (!Number.isFinite(num)) {
    return null;
  }
  return LABEL_NOTA_REUNIAO[num] || String(num);
}

function renderCelulaNotaReuniao(nota) {
  const label = formatarNotaReuniao(nota);
  if (!label) {
    return <span className="ltm-muted">—</span>;
  }
  return label;
}

const REUNIOES_AVALIACOES_COLS = [
  { key: "nome", label: "Convidado", excelLabel: "Convidado" },
  {
    key: "empresa",
    label: "Empresa",
    excelLabel: "Empresa",
    excelValue: (row) => row.empresa || "",
    render: (row) =>
      row.empresa ? (
        row.empresa
      ) : (
        <span className="ltm-muted">Não informada</span>
      ),
  },
  {
    key: "cargo",
    label: "Cargo",
    excelLabel: "Cargo",
    excelValue: (row) => row.cargo || "",
    render: (row) =>
      row.cargo ? row.cargo : <span className="ltm-muted">Não informado</span>,
  },
  {
    key: "patrocinador",
    label: "Patrocinador",
    excelLabel: "Patrocinador",
    excelValue: (row) => row.patrocinador || "",
    render: (row) =>
      row.patrocinador && row.patrocinador !== "—" ? (
        row.patrocinador
      ) : (
        <span className="ltm-muted">—</span>
      ),
  },
  {
    key: "notaConvidado",
    label: "Nota convidado",
    excelLabel: "Nota convidado",
    excelValue: (row) => formatarNotaReuniao(row.notaConvidado) || "",
    excelEmptyValue: "—",
    render: (row) => renderCelulaNotaReuniao(row.notaConvidado),
  },
  {
    key: "notaPatrocinador",
    label: "Nota patrocinador",
    excelLabel: "Nota patrocinador",
    excelValue: (row) => formatarNotaReuniao(row.notaPatrocinador) || "",
    excelEmptyValue: "—",
    render: (row) => renderCelulaNotaReuniao(row.notaPatrocinador),
  },
];

function formatarHoraPadraoSala(dtHoraInicio) {
  if (!dtHoraInicio) {
    return "";
  }
  const hora = formatarHora(String(dtHoraInicio));
  if (!hora || hora.length < 5) {
    return "";
  }
  return `${hora.substring(0, 2)}H${hora.substring(3, 5)}`;
}

const AGENDA_EXTRAORDINARIA_PARTICIPANTES_COLS = [
  ...CREDENCIADO_LISTA_COLS,
  {
    key: "sala",
    label: "Sala",
    excelLabel: "Sala",
    excelValue: (row) => {
      if (!row.sala) {
        return "";
      }
      const horario = formatarHoraPadraoSala(row.dtHoraInicio);
      return horario ? `${row.sala} (${horario})` : row.sala;
    },
    render: (row) => {
      if (!row.sala) {
        return <span className="ltm-muted">Não informada</span>;
      }
      const horario = formatarHoraPadraoSala(row.dtHoraInicio);
      return (
        <>
          <div>{row.sala}</div>
          {horario ? <div className="ltm-muted">{horario}</div> : null}
        </>
      );
    },
  },
  ...COLUNAS_PESQUISA_CONTEUDO,
];

const DASHBOARD_VAZIO = {
  credenciamento: {
    participantes: 0,
    patrocinadores: 0,
    patrocinadoresSemParticipantes: {
      total: 0,
      porPlano: [],
    },
    total: 0,
    porDia: [],
  },
  acaoEspecial: {
    titulo: "Degustação de caviar",
    totalCredenciados: 0,
    totalComResposta: 0,
    opcoes: [],
  },
  estatisticasCredenciamento: ESTATISTICAS_VAZIAS,
  reunioes: {
    agendadas: 0,
    executadas: 0,
    naoEntregues: 0,
    noShow: 0,
    extras: 0,
    naoAvaliadas: 0,
  },
  salasConteudo: {
    editorial: {
      salas: 0,
      escolhas: 0,
      porSala: [],
      totais: {
        inscritos: 0,
        checkins: 0,
        avaliacoesRecebidas: 0,
        avaliacoesPendentes: 0,
        avaliacaoMedia: null,
      },
    },
    patrocinado: {
      salas: 0,
      escolhas: 0,
      porSala: [],
      totais: {
        inscritos: 0,
        checkins: 0,
        avaliacoesRecebidas: 0,
        avaliacoesPendentes: 0,
        avaliacaoMedia: null,
      },
    },
  },
  agendaExtraordinaria: {
    salas: 0,
    escolhasCredenciamento: 0,
    inscritosTotal: 0,
    inscritosConvidados: 0,
    inscritosParticipantesPatrocinador: 0,
    porSala: [],
    porTipoParticipante: [],
  },
  topEmpresasMatchMeet: [],
  ultimosConvidados: [],
  interacoes: INTERACOES_VAZIAS,
};

const ABA_CREDENCIAMENTO = "credenciamento";
const ABA_ESTATISTICAS = "estatisticas";
const ABA_REUNIOES = "reunioes";
const ABA_AGENDAMENTOS = "agendamentos";
const ABA_INTERACOES = "interacoes";

function fmt(n) {
  return Number(n ?? 0).toLocaleString("pt-BR");
}

function KpiCard({
  label,
  value,
  hint,
  icon,
  tone = "blue",
  to,
  linkLabel = "Mais informações",
  compact = false,
}) {
  const className = [
    "adb-kpi",
    `adb-kpi--tone-${tone}`,
    compact ? "adb-kpi--compact" : "",
    to ? "adb-kpi--has-footer" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className}>
      <div className="adb-kpi-body">
        <p className="adb-kpi-label">{label}</p>
        <div className="adb-kpi-metric">
          <p className="adb-kpi-value">{value}</p>
          <div className="adb-kpi-icon-watermark" aria-hidden>
            {icon}
          </div>
        </div>
        {hint ? <p className="adb-kpi-hint">{hint}</p> : null}
      </div>
      {to ? (
        <Link to={to} className="adb-kpi-footer">
          <span>{linkLabel}</span>
          <FaArrowCircleRight size={14} aria-hidden />
        </Link>
      ) : null}
    </article>
  );
}

const CHART_COLORS = {
  participantes: "#0d1d41",
  patrocinadores: "#008c39",
};

const ACAO_ESPECIAL_COLORS = {
  sim: "#008c39",
  nao: "#64748b",
  nao_informado: "#cbd5e1",
};

function AcaoEspecialDonutTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div className="adb-chart-tooltip">
      <p className="adb-chart-tooltip-title">{item.label}</p>
      <p>
        Quantidade: <strong>{fmt(item.quantidade)}</strong>
      </p>
      {item.percentual != null ? (
        <p>
          Participação: <strong>{item.percentual}%</strong>
        </p>
      ) : null}
    </div>
  );
}

function credenciadoBadgeVariant(tipo, fallback = "convidado") {
  return tipo === "participante_patrocinador" ? "patrocinador" : fallback;
}

function CredenciadoBadge({ participante, variant, onNavigate }) {
  const badgeVariant =
    variant ?? credenciadoBadgeVariant(participante.tipo);

  return (
    <Link
      to={`/private/adm/convidados/editar/${participante.id}`}
      className={`adb-cred-badge adb-cred-badge--${badgeVariant} adb-cred-badge--link`}
      onClick={() => onNavigate?.()}
      title={`Editar cadastro de ${participante.nome}`}
    >
      <span className="adb-cred-badge-name">{participante.nome}</span>
      {participante.empresa ? (
        <span className="adb-cred-badge-empresa">{participante.empresa}</span>
      ) : (
        <span className="adb-cred-badge-empresa adb-cred-badge-empresa--muted">
          Empresa não informada
        </span>
      )}
    </Link>
  );
}

function CredenciadoBadgeList({
  items,
  emptyMessage,
  getVariant,
  keyPrefix = "cred",
  onItemClick,
}) {
  if (!items.length) {
    return <p className="adb-cred-modal-empty">{emptyMessage}</p>;
  }

  return (
    <div className="adb-cred-modal-badges">
      {items.map((p) => (
        <CredenciadoBadge
          key={`${keyPrefix}-${p.id}`}
          participante={p}
          variant={getVariant?.(p)}
          onNavigate={onItemClick}
        />
      ))}
    </div>
  );
}

function AcaoEspecialDonutChart({ dados, onInteresseClick }) {
  const chartData = useMemo(() => {
    const opcoes = dados?.opcoes ?? [];
    const baseTotal =
      opcoes.reduce((acc, item) => acc + Number(item.quantidade ?? 0), 0) || 1;

    return opcoes.map((item) => ({
      ...item,
      quantidade: Number(item.quantidade ?? 0),
      percentual: Math.round((Number(item.quantidade ?? 0) / baseTotal) * 100),
    }));
  }, [dados]);

  const totalComResposta = Number(dados?.totalComResposta ?? 0);
  const interesseSim =
    chartData.find((item) => item.opcao === "sim")?.quantidade ?? 0;

  if (!chartData.length) {
    return (
      <div className="apl-card adb-acao-card">
        <h3 className="adb-split-side-title">Ação especial</h3>
        <p className="adb-acao-empty">Nenhuma resposta registrada ainda.</p>
      </div>
    );
  }

  return (
    <div className="apl-card adb-acao-card">
      <div className="adb-acao-head">
        <div>
          <h3 className="adb-split-side-title">Ação especial</h3>
          <p className="adb-acao-subtitle">{dados?.titulo}</p>
        </div>
        {interesseSim > 0 ? (
          <button
            type="button"
            className="adb-acao-total adb-acao-total--clickable"
            title="Ver credenciados com interesse (Sim)"
            onClick={onInteresseClick}
          >
            {fmt(interesseSim)} com interesse
          </button>
        ) : (
          <span className="adb-acao-total" title="Credenciados com interesse (Sim)">
            {fmt(interesseSim)} com interesse
          </span>
        )}
      </div>

      <div
        className="adb-acao-chart-wrap"
        role="img"
        aria-label={`Gráfico de rosca da ação especial. ${totalComResposta} credenciados responderam.`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="quantidade"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="82%"
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
            >
              {chartData.map((item) => (
                <Cell
                  key={item.opcao}
                  fill={ACAO_ESPECIAL_COLORS[item.opcao] ?? "#94a3b8"}
                />
              ))}
            </Pie>
            <Tooltip
              content={<AcaoEspecialDonutTooltip />}
              wrapperStyle={{ zIndex: 30, outline: 'none' }}
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)',
                padding: 0,
                margin: 0,
              }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              formatter={(value, entry) =>
                `${value} (${fmt(entry?.payload?.quantidade ?? 0)})`
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="adb-acao-center" aria-hidden>
          <span className="adb-acao-center-value">{fmt(totalComResposta)}</span>
          <span className="adb-acao-center-label">respostas</span>
        </div>
      </div>
    </div>
  );
}

function CredenciamentoChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const participantes = payload.find((item) => item.dataKey === "participantes")?.value ?? 0;
  const patrocinadores = payload.find((item) => item.dataKey === "patrocinadores")?.value ?? 0;
  const total = Number(participantes) + Number(patrocinadores);

  return (
    <div className="adb-chart-tooltip">
      <p className="adb-chart-tooltip-title">{label}</p>
      <p className="adb-chart-tooltip-row">
        <span className="adb-chart-tooltip-dot adb-chart-tooltip-dot--part" aria-hidden />
        Participantes: <strong>{fmt(participantes)}</strong>
      </p>
      <p className="adb-chart-tooltip-row">
        <span className="adb-chart-tooltip-dot adb-chart-tooltip-dot--patr" aria-hidden />
        Patrocinadores: <strong>{fmt(patrocinadores)}</strong>
      </p>
      <p className="adb-chart-tooltip-total">
        Total: <strong>{fmt(total)}</strong>
      </p>
      <p className="adb-chart-tooltip-hint">Clique na coluna para ver os nomes</p>
    </div>
  );
}

function CredenciamentoDiaModal({
  show,
  diaLabel,
  onClose,
  participantes,
  patrocinadores,
  loading,
  error,
}) {
  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="titulo-modal">
          Credenciamentos em {diaLabel}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="adb-cred-modal-body">
        {loading ? (
          <div className="apl-loading" aria-live="polite">
            <FaSpinner className="fa-spin" size={24} aria-hidden />
            <p>Carregando credenciados…</p>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="adb-cred-modal-error" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error ? (
          <div className="adb-cred-modal-sections">
            <section className="adb-cred-modal-section">
              <h3>
                <FaUsers size={14} aria-hidden />
                Participantes ({participantes.length})
              </h3>
              <CredenciadoBadgeList
                items={participantes}
                keyPrefix="part"
                emptyMessage="Nenhum participante neste dia."
                getVariant={() => "convidado"}
                onItemClick={onClose}
              />
            </section>

            <section className="adb-cred-modal-section">
              <h3>
                <FaBuilding size={14} aria-hidden />
                Patrocinadores ({patrocinadores.length})
              </h3>
              <CredenciadoBadgeList
                items={patrocinadores}
                keyPrefix="patr"
                emptyMessage="Nenhum patrocinador neste dia."
                getVariant={() => "patrocinador"}
                onItemClick={onClose}
              />
            </section>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}

function CredenciamentoPorDiaChart({ porDia, onDayClick }) {
  const chartData = useMemo(
    () =>
      (porDia ?? []).map((item) => ({
        dia: item.dia,
        data: item.data,
        participantes: Number(item.participantes ?? 0),
        patrocinadores: Number(item.patrocinadores ?? 0),
        total:
          Number(item.participantes ?? 0) + Number(item.patrocinadores ?? 0),
      })),
    [porDia],
  );

  const handleChartClick = (state) => {
    const payload = state?.activePayload?.[0]?.payload;
    if (payload?.data && onDayClick) {
      onDayClick(payload);
    }
  };

  if (!chartData.length) {
    return (
      <div className="apl-card adb-chart-card adb-chart-card--ratio">
        <div className="adb-chart-header">
          <h3>Credenciamento por dia</h3>
        </div>
        <p className="adb-empty-chart">Nenhum credenciamento registrado por dia.</p>
      </div>
    );
  }

  return (
    <div className="apl-card adb-chart-card adb-chart-card--ratio">
      <div className="adb-chart-header">
        <div>
          <h3>Credenciamento por dia</h3>
          <p className="adb-chart-subtitle">Clique em um dia para ver os credenciados</p>
        </div>
        <div className="adb-chart-legend" aria-hidden>
          <span className="adb-chart-legend-item">
            <span className="adb-chart-legend-dot adb-chart-legend-dot--part" />
            Participantes
          </span>
          <span className="adb-chart-legend-item">
            <span className="adb-chart-legend-dot adb-chart-legend-dot--patr" />
            Patrocinadores
          </span>
        </div>
      </div>
      <div
        className="adb-chart-body adb-chart-body--clickable"
        role="img"
        aria-label="Gráfico de barras do credenciamento por dia do evento. Clique em um dia para ver os credenciados."
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
            barCategoryGap="20%"
            barGap={4}
            onClick={handleChartClick}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e2e8f0"
            />
            <XAxis
              dataKey="dia"
              tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={8}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              content={<CredenciamentoChartTooltip />}
              cursor={{ fill: "rgba(13, 29, 65, 0.04)" }}
            />
            <Bar
              dataKey="participantes"
              name="Participantes"
              fill={CHART_COLORS.participantes}
              radius={[6, 6, 0, 0]}
              maxBarSize={36}
            />
            <Bar
              dataKey="patrocinadores"
              name="Patrocinadores"
              fill={CHART_COLORS.patrocinadores}
              radius={[6, 6, 0, 0]}
              maxBarSize={36}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function pctSeguro(parte, total) {
  if (!total || total <= 0) {
    return 0;
  }
  return Math.round((Number(parte) / Number(total)) * 100);
}

function fmtMedia(valor) {
  if (valor == null || Number.isNaN(Number(valor))) {
    return "—";
  }
  return Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function SalaIndicadorMetric({
  label,
  value,
  hint,
  tone = "slate",
  onClick,
}) {
  const clickable = typeof onClick === "function";
  const className = [
    "adb-sci-metric",
    `adb-sci-metric--${tone}`,
    clickable ? "adb-sci-metric--clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (clickable) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        title="Ver lista e baixar"
      >
        <p className="adb-sci-metric-label">{label}</p>
        <p className="adb-sci-metric-value">{value}</p>
        {hint ? <p className="adb-sci-metric-hint">{hint}</p> : null}
      </button>
    );
  }

  return (
    <article className={className}>
      <p className="adb-sci-metric-label">{label}</p>
      <p className="adb-sci-metric-value">{value}</p>
      {hint ? <p className="adb-sci-metric-hint">{hint}</p> : null}
    </article>
  );
}

function AgendaExtraordinariaIndicadoresSection({
  dados,
  onSalaClick,
  onInscritosClick,
}) {
  const [salaId, setSalaId] = useState(null);
  const salas = useMemo(() => dados?.porSala ?? [], [dados?.porSala]);

  useEffect(() => {
    if (!salas.length) {
      setSalaId(null);
      return;
    }
    if (!salas.some((sala) => sala.id === salaId)) {
      setSalaId(salas[0].id);
    }
  }, [salas, salaId]);

  const selecionada = useMemo(
    () => salas.find((sala) => sala.id === salaId) ?? null,
    [salas, salaId],
  );

  if (!salas.length) {
    return (
      <div className="adb-sci-section adb-ext-section">
        <h3 className="adb-section-title">Agenda extraordinária</h3>
        <p className="adb-sci-empty">
          Nenhuma agenda extraordinária cadastrada no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="adb-sci-section adb-ext-section">
      <div className="adb-sci-head">
        <div>
          <h3 className="adb-section-title">Agenda extraordinária</h3>
          <p className="adb-sci-intro">
            Interesse na ação especial e inscritos por sala extraordinária.
          </p>
        </div>
      </div>

      <div className="adb-sci-summary adb-ext-summary" aria-label="Totais da agenda extraordinária">
        <SalaIndicadorMetric
          label="Ação especial (Sim)"
          value={fmt(dados?.escolhasCredenciamento)}
          hint="Interessados na ação especial"
          tone="amber"
        />
        <SalaIndicadorMetric
          label="Inscritos em todas as salas"
          value={fmt(dados?.inscritosTotal)}
          hint={`${fmt(dados?.salas)} sala${(dados?.salas ?? 0) === 1 ? "" : "s"} · clique para ver e baixar`}
          tone="blue"
          onClick={onInscritosClick}
        />
        <SalaIndicadorMetric
          label="Convidados inscritos"
          value={fmt(dados?.inscritosConvidados)}
          hint={`${fmt(dados?.salas)} sala${(dados?.salas ?? 0) === 1 ? "" : "s"}`}
          tone="slate"
        />
        <SalaIndicadorMetric
          label="Participantes patrocinadores"
          value={fmt(dados?.inscritosParticipantesPatrocinador)}
          hint="Inscritos nas salas extraordinárias"
          tone="green"
        />
      </div>

      <div className="adb-sci-panel">
        <div className="adb-sci-picker" role="listbox" aria-label="Salas extraordinárias">
          {salas.map((sala) => {
            const ativa = sala.id === salaId;
            return (
              <button
                key={sala.id}
                type="button"
                role="option"
                aria-selected={ativa}
                className={`adb-sci-chip${ativa ? " adb-sci-chip--active" : ""} adb-sci-chip--extraordinaria`}
                onClick={() => setSalaId(sala.id)}
                title={sala.nome}
              >
                <span className="adb-sci-chip-tipo">Extraordinária</span>
                <span className="adb-sci-chip-nome">{sala.nome}</span>
                <span className="adb-sci-chip-meta">
                  {fmt(sala.inscritos)} inscritos
                </span>
              </button>
            );
          })}
        </div>

        {selecionada ? (
          <div className="adb-sci-detail" key={selecionada.id} aria-live="polite">
            <div className="adb-sci-detail-head">
              <div>
                <p className="adb-sci-detail-tipo">Extraordinária</p>
                <h4 className="adb-sci-detail-title">{selecionada.nome}</h4>
                <p className="adb-sci-detail-meta">
                  {selecionada.local ? (
                    <>
                      <FaMapMarkerAlt size={11} aria-hidden />{" "}
                      {selecionada.local}
                    </>
                  ) : (
                    "Local não informado"
                  )}
                  {selecionada.dtHoraInicio
                    ? ` · ${formatarDiaHora(selecionada.dtHoraInicio)}`
                    : ""}
                </p>
              </div>
              {typeof onSalaClick === "function" ? (
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost adb-sci-detail-link"
                  onClick={() => onSalaClick(selecionada)}
                >
                  <FaUsers size={13} aria-hidden />
                  Ver inscritos
                </button>
              ) : null}
            </div>

            <div className="adb-sci-detail-grid adb-ext-detail-grid">
              <SalaIndicadorMetric
                label="Inscritos nesta sala"
                value={fmt(selecionada.inscritos)}
                tone="blue"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SalasConteudoIndicadoresSection({
  editorial,
  patrocinado,
  onSalaClick,
}) {
  const [filtro, setFiltro] = useState("todas");
  const [salaId, setSalaId] = useState(null);

  const salas = useMemo(() => {
    const editoriais = (editorial?.porSala ?? []).map((sala) => ({
      ...sala,
      tipo: "editorial",
      tipoLabel: "Editorial",
    }));
    const patrocinadas = (patrocinado?.porSala ?? []).map((sala) => ({
      ...sala,
      tipo: "patrocinado",
      tipoLabel: "Patrocinado",
    }));

    if (filtro === "editorial") {
      return editoriais;
    }
    if (filtro === "patrocinado") {
      return patrocinadas;
    }
    return [...editoriais, ...patrocinadas];
  }, [editorial, patrocinado, filtro]);

  useEffect(() => {
    if (!salas.length) {
      setSalaId(null);
      return;
    }
    if (!salas.some((sala) => sala.id === salaId)) {
      setSalaId(salas[0].id);
    }
  }, [salas, salaId]);

  const selecionada = useMemo(
    () => salas.find((sala) => sala.id === salaId) ?? null,
    [salas, salaId],
  );

  const totaisFiltro = useMemo(() => {
    if (filtro === "editorial") {
      return editorial?.totais;
    }
    if (filtro === "patrocinado") {
      return patrocinado?.totais;
    }

    const ed = editorial?.totais ?? {};
    const pat = patrocinado?.totais ?? {};
    const inscritos = Number(ed.inscritos ?? 0) + Number(pat.inscritos ?? 0);
    const checkins = Number(ed.checkins ?? 0) + Number(pat.checkins ?? 0);
    const avaliacoesRecebidas =
      Number(ed.avaliacoesRecebidas ?? 0) + Number(pat.avaliacoesRecebidas ?? 0);
    const avaliacoesPendentes =
      Number(ed.avaliacoesPendentes ?? 0) + Number(pat.avaliacoesPendentes ?? 0);

    let media = null;
    if (avaliacoesRecebidas > 0) {
      const soma =
        Number(ed.avaliacaoMedia ?? 0) * Number(ed.avaliacoesRecebidas ?? 0) +
        Number(pat.avaliacaoMedia ?? 0) * Number(pat.avaliacoesRecebidas ?? 0);
      media = Math.round((soma / avaliacoesRecebidas) * 10) / 10;
    }

    return {
      inscritos,
      checkins,
      avaliacoesRecebidas,
      avaliacoesPendentes,
      avaliacaoMedia: media,
    };
  }, [editorial, patrocinado, filtro]);

  const checkinPct = pctSeguro(selecionada?.checkins, selecionada?.inscritos);
  const avaliacaoPct = pctSeguro(
    selecionada?.avaliacoesRecebidas,
    selecionada?.checkins,
  );
  const temPatrocinado = (patrocinado?.salas ?? 0) > 0;

  if (!salas.length && filtro === "todas") {
    return (
      <div className="adb-sci-section">
        <h3 className="adb-section-title">Salas de conteúdo</h3>
        <p className="adb-sci-empty">
          Nenhuma sala de conteúdo cadastrada no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="adb-sci-section">
      <div className="adb-sci-head">
        <div>
          <h3 className="adb-section-title">Salas de conteúdo</h3>
          <p className="adb-sci-intro">
            Indicadores por sala: inscritos, check-in e avaliações. Selecione
            uma sala para detalhar.
          </p>
        </div>
        <div className="adb-sci-filters" role="tablist" aria-label="Filtro de salas">
          <button
            type="button"
            role="tab"
            aria-selected={filtro === "todas"}
            className={`adb-sci-filter${filtro === "todas" ? " adb-sci-filter--active" : ""}`}
            onClick={() => setFiltro("todas")}
          >
            Todas
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filtro === "editorial"}
            className={`adb-sci-filter${filtro === "editorial" ? " adb-sci-filter--active" : ""}`}
            onClick={() => setFiltro("editorial")}
          >
            Editorial
          </button>
          {temPatrocinado ? (
            <button
              type="button"
              role="tab"
              aria-selected={filtro === "patrocinado"}
              className={`adb-sci-filter${filtro === "patrocinado" ? " adb-sci-filter--active" : ""}`}
              onClick={() => setFiltro("patrocinado")}
            >
              Patrocinado
            </button>
          ) : null}
        </div>
      </div>

      <div className="adb-sci-summary" aria-label="Totais do filtro">
        <SalaIndicadorMetric
          label="Inscritos"
          value={fmt(totaisFiltro?.inscritos)}
          tone="blue"
        />
        <SalaIndicadorMetric
          label="Check-ins"
          value={fmt(totaisFiltro?.checkins)}
          hint={`${pctSeguro(totaisFiltro?.checkins, totaisFiltro?.inscritos)}% dos inscritos`}
          tone="green"
        />
        <SalaIndicadorMetric
          label="Avaliação média"
          value={fmtMedia(totaisFiltro?.avaliacaoMedia)}
          tone="amber"
        />
        <SalaIndicadorMetric
          label="Avaliações"
          value={fmt(totaisFiltro?.avaliacoesRecebidas)}
          hint={`${fmt(totaisFiltro?.avaliacoesPendentes)} pendentes`}
          tone="slate"
        />
      </div>

      {!salas.length ? (
        <p className="adb-sci-empty">Nenhuma sala neste filtro.</p>
      ) : (
        <div className="adb-sci-panel">
          <div className="adb-sci-picker" role="listbox" aria-label="Salas">
            {salas.map((sala) => {
              const ativa = sala.id === salaId;
              const checkPct = pctSeguro(sala.checkins, sala.inscritos);
              return (
                <button
                  key={sala.id}
                  type="button"
                  role="option"
                  aria-selected={ativa}
                  className={`adb-sci-chip${ativa ? " adb-sci-chip--active" : ""} adb-sci-chip--${sala.tipo}`}
                  onClick={() => setSalaId(sala.id)}
                  title={sala.nome}
                >
                  <span className="adb-sci-chip-tipo">{sala.tipoLabel}</span>
                  <span className="adb-sci-chip-nome">{sala.nome}</span>
                  <span className="adb-sci-chip-meta">
                    {fmt(sala.inscritos)} inscritos · {checkPct}% check-in
                  </span>
                  <span
                    className="adb-sci-chip-bar"
                    aria-hidden
                    style={{ "--adb-sci-pct": `${checkPct}%` }}
                  />
                </button>
              );
            })}
          </div>

          {selecionada ? (
            <div
              className="adb-sci-detail"
              key={selecionada.id}
              aria-live="polite"
            >
              <div className="adb-sci-detail-head">
                <div>
                  <p className="adb-sci-detail-tipo">{selecionada.tipoLabel}</p>
                  <h4 className="adb-sci-detail-title">{selecionada.nome}</h4>
                  <p className="adb-sci-detail-meta">
                    {selecionada.local ? (
                      <>
                        <FaMapMarkerAlt size={11} aria-hidden />{" "}
                        {selecionada.local}
                      </>
                    ) : (
                      "Local não informado"
                    )}
                    {selecionada.dtHoraInicio
                      ? ` · ${formatarDiaHora(selecionada.dtHoraInicio)}`
                      : ""}
                  </p>
                </div>
                {typeof onSalaClick === "function" ? (
                  <button
                    type="button"
                    className="apl-btn apl-btn--ghost adb-sci-detail-link"
                    onClick={() => onSalaClick(selecionada)}
                  >
                    <FaUsers size={13} aria-hidden />
                    Ver inscritos
                  </button>
                ) : null}
              </div>

              <div className="adb-sci-detail-grid">
                <SalaIndicadorMetric
                  label="Inscritos"
                  value={fmt(selecionada.inscritos ?? selecionada.participantes)}
                  tone="blue"
                />
                <SalaIndicadorMetric
                  label="Check-ins"
                  value={fmt(selecionada.checkins)}
                  hint={`${checkinPct}% dos inscritos`}
                  tone="green"
                />
                <SalaIndicadorMetric
                  label="Avaliação média"
                  value={fmtMedia(selecionada.avaliacaoMedia)}
                  hint={
                    selecionada.avaliacoesRecebidas > 0
                      ? `${fmt(selecionada.avaliacoesRecebidas)} respostas`
                      : "Sem avaliações"
                  }
                  tone="amber"
                />
                <SalaIndicadorMetric
                  label="Avaliações recebidas"
                  value={fmt(selecionada.avaliacoesRecebidas)}
                  tone="slate"
                />
                <SalaIndicadorMetric
                  label="Avaliações pendentes"
                  value={fmt(selecionada.avaliacoesPendentes)}
                  hint="Com check-in e sem resposta"
                  tone="red"
                />
              </div>

              <div className="adb-sci-progress">
                <div className="adb-sci-progress-item">
                  <div className="adb-sci-progress-labels">
                    <span>
                      <FaUserCheck size={12} aria-hidden /> Check-in
                    </span>
                    <strong>{checkinPct}%</strong>
                  </div>
                  <div className="adb-sci-progress-track">
                    <div
                      className="adb-sci-progress-fill adb-sci-progress-fill--checkin"
                      style={{ width: `${checkinPct}%` }}
                    />
                  </div>
                </div>
                <div className="adb-sci-progress-item">
                  <div className="adb-sci-progress-labels">
                    <span>
                      <FaStar size={12} aria-hidden /> Avaliações (sobre check-ins)
                    </span>
                    <strong>{avaliacaoPct}%</strong>
                  </div>
                  <div className="adb-sci-progress-track">
                    <div
                      className="adb-sci-progress-fill adb-sci-progress-fill--rate"
                      style={{ width: `${avaliacaoPct}%` }}
                    />
                  </div>
                </div>
                <div className="adb-sci-progress-item">
                  <div className="adb-sci-progress-labels">
                    <span>
                      <FaClipboardList size={12} aria-hidden /> Pendências
                    </span>
                    <strong>{fmt(selecionada.avaliacoesPendentes)}</strong>
                  </div>
                  <div className="adb-sci-progress-track">
                    <div
                      className="adb-sci-progress-fill adb-sci-progress-fill--pending"
                      style={{
                        width: `${pctSeguro(
                          selecionada.avaliacoesPendentes,
                          selecionada.checkins || selecionada.avaliacoesPendentes,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SalaConteudoCard({
  titulo,
  dados,
  tipo,
  maxEscolhas,
  exibirPorSala = false,
  onSalaClick,
}) {
  const pct = maxEscolhas
    ? Math.round((dados.escolhas / maxEscolhas) * 100)
    : 0;
  const porSala = Array.isArray(dados.porSala) ? dados.porSala : [];

  return (
    <div className="apl-card adb-sala-card adb-sala-card--stack">
      <h3>{titulo}</h3>
      <div className="adb-sala-metrics">
        <div>
          <p className="adb-sala-metric-label">Salas disponíveis</p>
          <p className="adb-sala-metric-value">{fmt(dados.salas)}</p>
        </div>
        <div>
          <p className="adb-sala-metric-label">Escolhas registradas</p>
          <p className="adb-sala-metric-value">{fmt(dados.escolhas)}</p>
        </div>
      </div>
      {exibirPorSala && porSala.length > 0 ? (
        <ul className="adb-sala-lista" aria-label={`Participantes por sala — ${titulo}`}>
          {porSala.map((sala) => (
            <li key={sala.id} className="adb-sala-lista-item">
              <button
                type="button"
                className="adb-sala-lista-btn"
                onClick={() => onSalaClick?.(sala)}
                title={`Ver convidados de ${sala.nome}`}
              >
                <span className="adb-sala-lista-nome" title={sala.nome}>
                  {sala.nome}
                </span>
                <span className="adb-sala-lista-qtd">
                  {fmt(sala.participantes)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="adb-sala-bar-wrap" aria-hidden>
        <div
          className={`adb-sala-bar-fill adb-sala-bar-fill--${tipo}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AdmDash() {
  const location = useLocation();
  const [dashboard, setDashboard] = useState(DASHBOARD_VAZIO);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalDia, setModalDia] = useState(null);
  const [credenciadosDia, setCredenciadosDia] = useState({
    participantes: [],
    patrocinadores: [],
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState(
    location.state?.aba || ABA_AGENDAMENTOS
  );
  const [modalAcaoInteresse, setModalAcaoInteresse] = useState(false);
  const [interessadosAcao, setInteressadosAcao] = useState([]);
  const [modalAcaoTitulo, setModalAcaoTitulo] = useState("");
  const [modalAcaoLoading, setModalAcaoLoading] = useState(false);
  const [modalAcaoError, setModalAcaoError] = useState(null);
  const [modalSala, setModalSala] = useState(null);
  const [participantesSala, setParticipantesSala] = useState([]);
  const [modalSalaLoading, setModalSalaLoading] = useState(false);
  const [modalSalaError, setModalSalaError] = useState(null);
  const [modalExtraordinaria, setModalExtraordinaria] = useState(false);
  const [participantesExtraordinaria, setParticipantesExtraordinaria] = useState(
    [],
  );
  const [modalExtraordinariaLoading, setModalExtraordinariaLoading] =
    useState(false);
  const [modalExtraordinariaError, setModalExtraordinariaError] = useState(null);
  const [modalReunioesAvaliacoes, setModalReunioesAvaliacoes] = useState(false);
  const [reunioesAvaliacoes, setReunioesAvaliacoes] = useState([]);
  const [modalReunioesAvaliacoesLoading, setModalReunioesAvaliacoesLoading] =
    useState(false);
  const [modalReunioesAvaliacoesError, setModalReunioesAvaliacoesError] =
    useState(null);

  useEffect(() => {
    if (location.state?.aba === ABA_AGENDAMENTOS) {
      setAbaAtiva(ABA_AGENDAMENTOS);
    }
  }, [location.state]);

  const carregarDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.get("/dashboard-adm");
      const payload = resp.data ?? {};

      setDashboard({
        credenciamento: payload.credenciamento ?? DASHBOARD_VAZIO.credenciamento,
        acaoEspecial: payload.acaoEspecial ?? DASHBOARD_VAZIO.acaoEspecial,
        estatisticasCredenciamento:
          payload.estatisticasCredenciamento ??
          DASHBOARD_VAZIO.estatisticasCredenciamento,
        reunioes: payload.reunioes ?? DASHBOARD_VAZIO.reunioes,
        salasConteudo: payload.salasConteudo ?? DASHBOARD_VAZIO.salasConteudo,
        agendaExtraordinaria:
          payload.agendaExtraordinaria ?? DASHBOARD_VAZIO.agendaExtraordinaria,
        topEmpresasMatchMeet:
          payload.topEmpresasMatchMeet ?? DASHBOARD_VAZIO.topEmpresasMatchMeet,
        ultimosConvidados:
          payload.ultimosConvidados ?? DASHBOARD_VAZIO.ultimosConvidados,
        interacoes: payload.interacoes ?? DASHBOARD_VAZIO.interacoes,
      });
      setAtualizadoEm(payload.atualizadoEm ?? null);
    } catch (err) {
      console.error("Erro ao carregar dashboard:", err);
      setError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os dados do dashboard.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDashboard();
  }, [carregarDashboard]);

  const abrirCredenciadosDia = useCallback(async (item) => {
    if (!item?.data) {
      return;
    }

    setModalDia({ dia: item.dia, data: item.data });
    setModalLoading(true);
    setModalError(null);
    setCredenciadosDia({ participantes: [], patrocinadores: [] });

    try {
      const resp = await api.get("/dashboard-adm-credenciados-dia", {
        params: { data: item.data },
      });
      setCredenciadosDia({
        participantes: resp.data?.participantes ?? [],
        patrocinadores: resp.data?.patrocinadores ?? [],
      });
    } catch (err) {
      console.error("Erro ao carregar credenciados do dia:", err);
      setModalError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os credenciados do dia.",
      );
    } finally {
      setModalLoading(false);
    }
  }, []);

  const fecharModalDia = useCallback(() => {
    setModalDia(null);
    setModalError(null);
  }, []);

  const abrirInteressadosAcao = useCallback(async () => {
    setModalAcaoInteresse(true);
    setModalAcaoLoading(true);
    setModalAcaoError(null);
    setInteressadosAcao([]);
    setModalAcaoTitulo(dashboard.acaoEspecial?.titulo ?? "Ação especial");

    try {
      const resp = await api.get("/dashboard-adm-acao-especial-interessados");
      setInteressadosAcao(resp.data?.interessados ?? []);
      setModalAcaoTitulo(resp.data?.titulo ?? dashboard.acaoEspecial?.titulo ?? "Ação especial");
    } catch (err) {
      console.error("Erro ao carregar interessados na ação especial:", err);
      setModalAcaoError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os interessados.",
      );
    } finally {
      setModalAcaoLoading(false);
    }
  }, [dashboard.acaoEspecial?.titulo]);

  const fecharModalAcaoInteresse = useCallback(() => {
    setModalAcaoInteresse(false);
    setModalAcaoError(null);
  }, []);

  const abrirParticipantesSala = useCallback(async (sala) => {
    if (!sala?.id) {
      return;
    }

    setModalSala({ id: sala.id, nome: sala.nome });
    setModalSalaLoading(true);
    setModalSalaError(null);
    setParticipantesSala([]);

    try {
      const resp = await api.get("/dashboard-adm-sala-conteudo-participantes", {
        params: { idAgenda: sala.id },
      });
      setParticipantesSala(resp.data?.participantes ?? []);
      if (resp.data?.sala?.nome) {
        setModalSala({ id: sala.id, nome: resp.data.sala.nome });
      }
    } catch (err) {
      console.error("Erro ao carregar participantes da sala:", err);
      setModalSalaError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os convidados desta sala.",
      );
    } finally {
      setModalSalaLoading(false);
    }
  }, []);

  const fecharModalSala = useCallback(() => {
    setModalSala(null);
    setModalSalaError(null);
  }, []);

  const abrirInscritosExtraordinaria = useCallback(async () => {
    setModalExtraordinaria(true);
    setModalExtraordinariaLoading(true);
    setModalExtraordinariaError(null);
    setParticipantesExtraordinaria([]);

    try {
      const resp = await api.get(
        "/dashboard-adm-agenda-extraordinaria-participantes",
      );
      setParticipantesExtraordinaria(resp.data?.participantes ?? []);
    } catch (err) {
      console.error("Erro ao carregar inscritos da agenda extraordinária:", err);
      setModalExtraordinariaError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar os inscritos das salas extraordinárias.",
      );
    } finally {
      setModalExtraordinariaLoading(false);
    }
  }, []);

  const fecharModalExtraordinaria = useCallback(() => {
    setModalExtraordinaria(false);
    setModalExtraordinariaError(null);
  }, []);

  const abrirReunioesAvaliacoes = useCallback(async () => {
    setModalReunioesAvaliacoes(true);
    setModalReunioesAvaliacoesLoading(true);
    setModalReunioesAvaliacoesError(null);
    setReunioesAvaliacoes([]);

    try {
      const resp = await api.get("/dashboard-adm-reunioes-avaliacoes");
      setReunioesAvaliacoes(resp.data?.reunioes ?? []);
    } catch (err) {
      console.error("Erro ao carregar reuniões com avaliações:", err);
      setModalReunioesAvaliacoesError(
        err?.response?.data?.mensagem ||
          "Não foi possível carregar as reuniões.",
      );
    } finally {
      setModalReunioesAvaliacoesLoading(false);
    }
  }, []);

  const fecharModalReunioesAvaliacoes = useCallback(() => {
    setModalReunioesAvaliacoes(false);
    setModalReunioesAvaliacoesError(null);
  }, []);

  const {
    credenciamento,
    acaoEspecial,
    estatisticasCredenciamento,
    reunioes,
    salasConteudo,
    agendaExtraordinaria,
    topEmpresasMatchMeet,
    ultimosConvidados,
    interacoes,
  } = dashboard;
  const maxEscolhasSalas =
    (salasConteudo.editorial?.escolhas || 0) +
    (salasConteudo.patrocinado?.escolhas || 0);

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form adb-page">
        <header className="apl-header" aria-label="Dashboard administrativo">
          <div className="apl-header-text">
            <h1>Dashboard</h1>
            <p>
              Visão consolidada de agendamentos, reuniões, interações,
              estatísticas e credenciamento do evento.
            </p>
          </div>
          <div className="apl-toolbar">
            {atualizadoEm ? (
              <span className="adb-updated-badge" title="Última atualização dos dados">
                Atualizado em {formatarDiaHora(atualizadoEm)}
              </span>
            ) : null}
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={carregarDashboard}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <FaSpinner className="fa-spin" size={12} aria-hidden />
              ) : (
                <FaSyncAlt size={12} aria-hidden />
              )}
              Atualizar
            </button>
          </div>
        </header>

        {error ? (
          <div className="adb-error-banner" role="alert">
            <p>{error}</p>
            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={carregarDashboard}
            >
              Tentar novamente
            </button>
          </div>
        ) : null}

        {isLoading && !error ? (
          <div className="apl-loading" aria-live="polite">
            <FaSpinner className="fa-spin" size={28} aria-hidden />
            <p>Carregando indicadores…</p>
          </div>
        ) : null}

        {!isLoading && !error ? (
          <div className={`adb-tabs-shell adb-tabs-shell--${abaAtiva}`}>
            <nav
              className="adb-tabs-nav"
              role="tablist"
              aria-label="Seções do dashboard"
            >
              <button
                type="button"
                role="tab"
                id="adb-tab-agendamentos"
                aria-selected={abaAtiva === ABA_AGENDAMENTOS}
                aria-controls="adb-panel-agendamentos"
                className={`adb-tab ${
                  abaAtiva === ABA_AGENDAMENTOS ? "adb-tab--active" : ""
                }`}
                onClick={() => setAbaAtiva(ABA_AGENDAMENTOS)}
              >
                <FaHandshake size={14} className="adb-tab-icon" aria-hidden />
                Agendamentos
              </button>
              <button
                type="button"
                role="tab"
                id="adb-tab-reunioes"
                aria-selected={abaAtiva === ABA_REUNIOES}
                aria-controls="adb-panel-reunioes"
                className={`adb-tab ${
                  abaAtiva === ABA_REUNIOES ? "adb-tab--active" : ""
                }`}
                onClick={() => setAbaAtiva(ABA_REUNIOES)}
              >
                <FaCalendarAlt size={14} className="adb-tab-icon" aria-hidden />
                Reuniões
              </button>
              <button
                type="button"
                role="tab"
                id="adb-tab-interacoes"
                aria-selected={abaAtiva === ABA_INTERACOES}
                aria-controls="adb-panel-interacoes"
                className={`adb-tab ${
                  abaAtiva === ABA_INTERACOES ? "adb-tab--active" : ""
                }`}
                onClick={() => setAbaAtiva(ABA_INTERACOES)}
              >
                <FaComments size={14} className="adb-tab-icon" aria-hidden />
                Interações
              </button>
              <button
                type="button"
                role="tab"
                id="adb-tab-estatisticas"
                aria-selected={abaAtiva === ABA_ESTATISTICAS}
                aria-controls="adb-panel-estatisticas"
                className={`adb-tab ${
                  abaAtiva === ABA_ESTATISTICAS ? "adb-tab--active" : ""
                }`}
                onClick={() => setAbaAtiva(ABA_ESTATISTICAS)}
              >
                <FaChartBar size={14} className="adb-tab-icon" aria-hidden />
                Estatísticas
              </button>
              <button
                type="button"
                role="tab"
                id="adb-tab-credenciamento"
                aria-selected={abaAtiva === ABA_CREDENCIAMENTO}
                aria-controls="adb-panel-credenciamento"
                className={`adb-tab ${
                  abaAtiva === ABA_CREDENCIAMENTO ? "adb-tab--active" : ""
                }`}
                onClick={() => setAbaAtiva(ABA_CREDENCIAMENTO)}
              >
                <FaUserCheck size={14} className="adb-tab-icon" aria-hidden />
                Credenciamento
              </button>
            </nav>

            {abaAtiva === ABA_CREDENCIAMENTO ? (
              <section
                className="adb-section adb-tab-panel"
                id="adb-panel-credenciamento"
                role="tabpanel"
                aria-labelledby="adb-tab-credenciamento"
              >
                <div className="adb-kpi-grid adb-kpi-grid--3 adb-kpi-grid--compact-row">
                  <KpiCard
                    compact
                    label="Participantes credenciados"
                    value={fmt(credenciamento.participantes)}
                    icon={<FaUsers size={18} />}
                    tone="blue"
                    to="/private/adm/convidados"
                    linkLabel="Ver participantes"
                  />
                  <KpiCard
                    compact
                    label="Patrocinadores credenciados"
                    value={fmt(credenciamento.patrocinadores)}
                    icon={<FaBuilding size={18} />}
                    tone="green"
                    to="/private/adm/patrocinadores"
                    linkLabel="Ver patrocinadores"
                  />
                  <KpiCard
                    compact
                    label="Total credenciados"
                    value={fmt(credenciamento.total)}
                    icon={<FaUserCheck size={18} />}
                    tone="slate"
                  />
                </div>

                <div
                  className="adb-split-84"
                  aria-label="Credenciamento por dia e ação especial"
                >
                  <div className="adb-split-chart">
                    <CredenciamentoPorDiaChart
                      porDia={credenciamento.porDia}
                      onDayClick={abrirCredenciadosDia}
                    />
                  </div>
                  <div className="adb-split-side">
                    <div className="adb-split-block">
                      <AcaoEspecialDonutChart
                        dados={acaoEspecial}
                        onInteresseClick={abrirInteressadosAcao}
                      />
                    </div>

                    <div className="adb-salas-section adb-salas-section--side">
                      <h3 className="adb-section-title">Salas de conteúdo</h3>
                      <div className="adb-salas-stack">
                        <SalaConteudoCard
                          titulo="Conteúdo editorial"
                          dados={salasConteudo.editorial}
                          tipo="editorial"
                          maxEscolhas={maxEscolhasSalas}
                          exibirPorSala
                          onSalaClick={abrirParticipantesSala}
                        />
                        {(salasConteudo.patrocinado?.salas ?? 0) > 0 ? (
                          <SalaConteudoCard
                            titulo="Conteúdo patrocinado"
                            dados={salasConteudo.patrocinado}
                            tipo="patrocinado"
                            maxEscolhas={maxEscolhasSalas}
                            exibirPorSala
                            onSalaClick={abrirParticipantesSala}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                <section
                  className="adb-sem-participantes"
                  aria-label="Patrocinadores sem participantes por plano"
                >
                  <div className="adb-sem-participantes-head">
                    <div className="adb-sem-participantes-title">
                      <FaExclamationTriangle
                        size={14}
                        className="adb-sem-participantes-icon"
                        aria-hidden
                      />
                      <h3>Patrocinadores sem participantes</h3>
                    </div>
                    <Link
                      to="/private/adm/patrocinadores"
                      className="adb-sem-participantes-link"
                    >
                      <span>Ver patrocinadores</span>
                      <FaArrowCircleRight size={14} aria-hidden />
                    </Link>
                  </div>
                  <div className="adb-sem-participantes-metrics">
                    <div className="adb-sem-participantes-metric adb-sem-participantes-metric--total">
                      <span className="adb-sem-participantes-value">
                        {fmt(credenciamento.patrocinadoresSemParticipantes?.total)}
                      </span>
                      <span className="adb-sem-participantes-label">Total</span>
                    </div>
                    {(
                      credenciamento.patrocinadoresSemParticipantes?.porPlano ??
                      []
                    ).map((plano) => (
                      <div
                        key={plano.chave}
                        className="adb-sem-participantes-metric"
                      >
                        <span className="adb-sem-participantes-value">
                          {fmt(plano.quantidade)}
                        </span>
                        <span
                          className="adb-sem-participantes-label"
                          title={plano.label}
                        >
                          {plano.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="adb-cred-duo">
                  <section
                    className="adb-match-meet-top"
                    aria-label="Top 5 empresas escolhidas no Match e Meet"
                  >
                    <div className="adb-match-meet-top-head">
                      <div className="adb-match-meet-top-title">
                        <FaHandshake
                          size={14}
                          className="adb-match-meet-top-icon"
                          aria-hidden
                        />
                        <h3>Top 5 Match &amp; Meet</h3>
                      </div>
                      <p className="adb-match-meet-top-subtitle">
                        Empresas mais escolhidas pelos participantes
                      </p>
                    </div>
                    {(topEmpresasMatchMeet ?? []).length > 0 ? (
                      <ol className="adb-match-meet-top-list">
                        {topEmpresasMatchMeet.map((empresa) => (
                          <li
                            key={`${empresa.posicao}-${empresa.nome}`}
                            className="adb-match-meet-top-item"
                          >
                            <span
                              className="adb-match-meet-top-pos"
                              aria-label={`Posição ${empresa.posicao}`}
                            >
                              {empresa.posicao}
                            </span>
                            <span
                              className="adb-match-meet-top-nome"
                              title={empresa.nome}
                            >
                              {empresa.nome}
                            </span>
                            <span className="adb-match-meet-top-qtd">
                              {fmt(empresa.escolhas)}
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="adb-match-meet-top-empty">
                        Nenhuma escolha de Match &amp; Meet registrada ainda.
                      </p>
                    )}
                  </section>

                  <section
                    className="adb-ultimos-convidados"
                    aria-label="Últimos 5 convidados credenciados"
                  >
                    <div className="adb-ultimos-convidados-head">
                      <div className="adb-ultimos-convidados-title">
                        <FaUsers
                          size={14}
                          className="adb-ultimos-convidados-icon"
                          aria-hidden
                        />
                        <h3>Últimos convidados</h3>
                      </div>
                      <p className="adb-ultimos-convidados-subtitle">
                        5 credenciamentos mais recentes
                      </p>
                    </div>
                    {(ultimosConvidados ?? []).length > 0 ? (
                      <ul className="adb-ultimos-convidados-list">
                        {ultimosConvidados.map((convidado) => (
                          <li
                            key={convidado.id}
                            className="adb-ultimos-convidados-item"
                          >
                            <div className="adb-ultimos-convidados-info">
                              <span
                                className="adb-ultimos-convidados-nome"
                                title={convidado.nome}
                              >
                                {convidado.nome}
                              </span>
                              {convidado.empresa ? (
                                <span
                                  className="adb-ultimos-convidados-empresa"
                                  title={convidado.empresa}
                                >
                                  {convidado.empresa}
                                </span>
                              ) : null}
                            </div>
                            <time
                              className="adb-ultimos-convidados-data"
                              dateTime={convidado.credenciadoEm}
                            >
                              {formatarDiaHora(convidado.credenciadoEm)}
                            </time>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="adb-ultimos-convidados-empty">
                        Nenhum convidado credenciado ainda.
                      </p>
                    )}
                  </section>
                </div>
              </section>
            ) : null}

            {abaAtiva === ABA_ESTATISTICAS ? (
              <AdmDashEstatisticas
                estatisticas={estatisticasCredenciamento}
                acaoEspecial={acaoEspecial}
              />
            ) : null}

            {abaAtiva === ABA_REUNIOES ? (
              <section
                className="adb-section adb-tab-panel"
                id="adb-panel-reunioes"
                role="tabpanel"
                aria-labelledby="adb-tab-reunioes"
              >
                <div className="adb-reunioes-section">
                  <h3 className="adb-section-title">Reuniões</h3>
                  <div className="adb-reunioes-layout adb-reunioes-layout--wide">
                    <div className="adb-reunioes-row adb-reunioes-row--trio">
                      <KpiCard
                        compact
                        label="Agendadas"
                        value={fmt(reunioes.agendadas)}
                        icon={<FaCalendarAlt size={18} />}
                        tone="blue"
                      />
                      <KpiCard
                        compact
                        label="Executadas"
                        value={fmt(reunioes.executadas)}
                        icon={<FaCheckCircle size={18} />}
                        tone="green"
                      />
                      <KpiCard
                        compact
                        label="Não entregues"
                        value={fmt(reunioes.naoEntregues)}
                        icon={<FaExclamationTriangle size={18} />}
                        tone="amber"
                        to="/private/adm/nao-entregue"
                        linkLabel="Ver pendências"
                      />
                    </div>
                  </div>
                </div>

                <div className="adb-ops-section">
                  <h3 id="adb-sec-ops" className="adb-section-title">
                    Indicadores operacionais
                  </h3>
                  <div className="adb-kpi-grid adb-kpi-grid--3 adb-kpi-grid--compact-row adb-kpi-grid--ops">
                    <KpiCard
                      compact
                      label="No shows"
                      value={fmt(reunioes.noShow)}
                      icon={<FaUserTimes size={18} />}
                      tone="red"
                    />
                    <KpiCard
                      compact
                      label="Reuniões extras"
                      value={fmt(reunioes.extras)}
                      icon={<FaPlusCircle size={18} />}
                      tone="slate"
                    />
                    <KpiCard
                      compact
                      label="Não avaliadas"
                      value={fmt(reunioes.naoAvaliadas)}
                      icon={<FaStarHalfAlt size={18} />}
                      tone="amber"
                      to="/private/adm/reuniao-resultado"
                      linkLabel="Avaliar"
                    />
                  </div>
                </div>

                <SalasConteudoIndicadoresSection
                  editorial={salasConteudo.editorial}
                  patrocinado={salasConteudo.patrocinado}
                  onSalaClick={abrirParticipantesSala}
                />

                <AgendaExtraordinariaIndicadoresSection
                  dados={agendaExtraordinaria}
                  onSalaClick={abrirParticipantesSala}
                  onInscritosClick={abrirInscritosExtraordinaria}
                />

                <section className="adb-footer-actions">
                  <button
                    type="button"
                    className="apl-btn apl-btn--secondary"
                    onClick={abrirReunioesAvaliacoes}
                  >
                    <FaClipboardList size={14} aria-hidden />
                    Reuniões por convidado
                  </button>
                  <button
                    type="button"
                    className="apl-btn apl-btn--ghost"
                    onClick={() => setAbaAtiva(ABA_AGENDAMENTOS)}
                  >
                    <FaHandshake size={14} aria-hidden />
                    Indicadores matching
                  </button>
                  <Link to="/private/adm/entregas-monitor" className="apl-btn apl-btn--ghost">
                    <FaBookOpen size={14} aria-hidden />
                    Monitor de entregas
                  </Link>
                  <Link to="/private/adm/reunioes" className="apl-btn apl-btn--ghost">
                    <FaBookOpen size={14} aria-hidden />
                    Gestão de reuniões
                  </Link>
                  <Link to="/private/adm/mais-detalhes" className="apl-btn apl-btn--ghost">
                    <FaExternalLinkAlt size={14} aria-hidden />
                    Mais detalhes
                  </Link>
                </section>
              </section>
            ) : null}

            {abaAtiva === ABA_AGENDAMENTOS ? <AdmDashAgendamentos /> : null}

            {abaAtiva === ABA_INTERACOES ? (
              <AdmDashInteracoes interacoes={interacoes} />
            ) : null}
          </div>
        ) : null}

        <CredenciamentoDiaModal
          show={Boolean(modalDia)}
          diaLabel={modalDia?.dia}
          onClose={fecharModalDia}
          participantes={credenciadosDia.participantes}
          patrocinadores={credenciadosDia.patrocinadores}
          loading={modalLoading}
          error={modalError}
        />

        <ListaTabelaModal
          show={modalAcaoInteresse}
          onClose={fecharModalAcaoInteresse}
          title={`Interessados — ${modalAcaoTitulo}`}
          subtitle="Credenciados que responderam Sim à ação especial."
          countLabel="Com interesse"
          columns={ACAO_ESPECIAL_INTERESSADOS_COLS}
          rows={interessadosAcao}
          loading={modalAcaoLoading}
          error={modalAcaoError}
          emptyMessage="Nenhum credenciado com interesse registrado."
          loadingMessage="Carregando interessados…"
          excelFileName={`acao_especial_interessados_${new Date().toISOString().slice(0, 10)}`}
          excelSheetName="Interessados"
          getRowHref={(row) => `/private/adm/convidados/editar/${row.id}`}
          size="xl"
        />

        <ListaTabelaModal
          show={Boolean(modalSala)}
          onClose={fecharModalSala}
          title={`Convidados — ${modalSala?.nome || "Sala de conteúdo"}`}
          subtitle="Participantes que escolheram este conteúdo."
          countLabel="Inscritos"
          columns={SALA_CONTEUDO_PARTICIPANTES_COLS}
          rows={participantesSala}
          loading={modalSalaLoading}
          error={modalSalaError}
          emptyMessage="Nenhum convidado escolheu esta sala ainda."
          loadingMessage="Carregando convidados…"
          excelFileName={`sala_conteudo_${(modalSala?.nome || "participantes")
            .toLowerCase()
            .replace(/[^a-z0-9]+/gi, "_")
            .replace(/^_|_$/g, "")}_${new Date().toISOString().slice(0, 10)}`}
          excelSheetName="Participantes"
          getRowHref={(row) => `/private/adm/convidados/editar/${row.id}`}
          size="xl"
        />

        <ListaTabelaModal
          show={modalExtraordinaria}
          onClose={fecharModalExtraordinaria}
          title="Inscritos — Agenda extraordinária"
          subtitle="Convidados e participantes patrocinadores inscritos em todas as salas."
          countLabel="Inscritos"
          columns={AGENDA_EXTRAORDINARIA_PARTICIPANTES_COLS}
          rows={participantesExtraordinaria}
          loading={modalExtraordinariaLoading}
          error={modalExtraordinariaError}
          emptyMessage="Nenhum inscrito nas salas extraordinárias."
          loadingMessage="Carregando inscritos…"
          excelFileName={`agenda_extraordinaria_inscritos_${new Date()
            .toISOString()
            .slice(0, 10)}`}
          excelSheetName="Inscritos"
          getRowHref={(row) => `/private/adm/convidados/editar/${row.id}`}
          size="xl"
        />

        <ListaTabelaModal
          show={modalReunioesAvaliacoes}
          onClose={fecharModalReunioesAvaliacoes}
          title="Reuniões por convidado"
          subtitle="Lista completa de reuniões com notas do convidado e do patrocinador."
          countLabel="Reuniões"
          columns={REUNIOES_AVALIACOES_COLS}
          rows={reunioesAvaliacoes}
          loading={modalReunioesAvaliacoesLoading}
          error={modalReunioesAvaliacoesError}
          emptyMessage="Nenhuma reunião encontrada."
          loadingMessage="Carregando reuniões…"
          excelFileName={`reunioes_por_convidado_${new Date()
            .toISOString()
            .slice(0, 10)}`}
          excelSheetName="Reuniões"
          getRowHref={(row) =>
            row.idConvidado
              ? `/private/adm/convidados/editar/${row.idConvidado}`
              : null
          }
          size="xl"
        />
      </div>
    </div>
  );
}

export default AdmDash;
